import { useReducer, useMemo, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const reducer = (state, { sortBy, clear }) => {
  if (clear) {
    return { sortBy: '', sortDirection: '' };
  }

  if (state.sortBy !== sortBy || state.sortBy === '') {
    return { sortBy, sortDirection: 'ASC' };
  }

  switch (state.sortDirection) {
    case '':
      return { sortBy, sortDirection: 'ASC' };
    case 'ASC':
      return { sortBy, sortDirection: 'DESC' };
    case 'DESC':
      return { sortBy: '', sortDirection: '' };
    default:
      return state;
  }
};

const getStorageKey = (pathname, storageKey = null) => {
  const baseKey = `sort_${pathname.replace(/\//g, '_').replace(/^_/, '')}`;
  return storageKey ? `${baseKey}_${storageKey}` : baseKey;
};

const saveToLocalStorage = (pathname, sortState, storageKey = null) => {
  try {
    const key = getStorageKey(pathname, storageKey);
    localStorage.setItem(key, JSON.stringify(sortState));
    return true;
  } catch {
    return null;
  }
};

const loadFromLocalStorage = (pathname, storageKey = null) => {
  try {
    const key = getStorageKey(pathname, storageKey);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const useSort = (options = {}) => {
  const { persistInUrl = false, storageKey = null } = options;

  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const previousSortState = useRef(null);

  // Initialize state from URL parameters or localStorage
  const urlSortBy = persistInUrl ? searchParams.get('sortBy') : null;
  const urlSortDirection = persistInUrl
    ? searchParams.get('sortDirection')
    : null;

  // Use the real browser pathname for localStorage key
  // to avoid cross-page collisions when multiple pages use HashRouter
  const browserPathname = window.location.pathname;

  // If no URL params but persistInUrl is enabled, try to load from localStorage
  const localStorageState =
    !urlSortBy && !urlSortDirection && persistInUrl
      ? loadFromLocalStorage(browserPathname, storageKey)
      : null;

  const initialState = {
    sortBy: urlSortBy || localStorageState?.sortBy || '',
    sortDirection: urlSortDirection || localStorageState?.sortDirection || '',
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  // Update URL and localStorage when sorting changes only if persistInUrl is true
  useEffect(() => {
    if (!persistInUrl) return;

    const currentSortState = `${state.sortBy}-${state.sortDirection}`;
    if (previousSortState.current === currentSortState) return;

    previousSortState.current = currentSortState;

    const updateUrl = () => {
      const newSearchParams = new URLSearchParams(location.search);

      if (state.sortBy && state.sortDirection) {
        newSearchParams.set('sortBy', state.sortBy);
        newSearchParams.set('sortDirection', state.sortDirection);
      } else {
        newSearchParams.delete('sortBy');
        newSearchParams.delete('sortDirection');
      }

      const newUrl = `${location.pathname}?${newSearchParams.toString()}`;
      history.replace(newUrl);
    };

    // Save to localStorage using the real browser pathname
    saveToLocalStorage(browserPathname, state, storageKey);

    updateUrl();
  }, [persistInUrl, history, location, storageKey, state, browserPathname]);

  const value = useMemo(
    () => ({
      sortingStatus: state,
      changeSort: sortBy => dispatch({ sortBy }),
      clearSort: () => dispatch({ clear: true }),
    }),
    [state],
  );

  return value;
};

export default useSort;
